import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  EmptyState,
  ErrorBanner,
  Icon,
  Skeleton,
} from "@/components/ui";
import { useAuth } from "@/context/AuthContext";
import { workspacesApi, type UserWorkspace } from "@/services/backend";
import {
  getRememberedTenantForUser,
  rememberTenantForUser,
  tenantMemoryKey,
} from "@/utils/tenantMemory";

export function TenantSelectionGate() {
  const { logout, setCurrentTenantId, user } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const userKey = useMemo(
    () => tenantMemoryKey(user?.id, user?.email),
    [user?.email, user?.id],
  );
  const [switchingTenantId, setSwitchingTenantId] = useState<string | null>(
    null,
  );
  const [switchError, setSwitchError] = useState<string | null>(null);
  const autoSwitchAttemptedRef = useRef(false);

  const workspaces = useQuery({
    queryKey: ["auth", "workspaces", "tenant-gate"],
    queryFn: () => workspacesApi.mine(),
    enabled: Boolean(user),
    staleTime: 30_000,
  });

  const selectTenant = useCallback(
    async (workspace: UserWorkspace, mode: "auto" | "manual") => {
      if (switchingTenantId) return;
      setSwitchError(null);
      setSwitchingTenantId(workspace.tenantId);
      try {
        const tokens = await workspacesApi.switch(workspace.tenantId);
        const tenantId = tokens.tenantId ?? workspace.tenantId;
        rememberTenantForUser(userKey, tenantId);
        setCurrentTenantId(tenantId);
        queryClient.clear();
        setSwitchingTenantId(null);
        navigate("/dashboard", { replace: true });
      } catch {
        setSwitchError(
          mode === "auto"
            ? "No pudimos restaurar tu último tenant. Selecciona uno para continuar."
            : "No pudimos activar este tenant. Intenta nuevamente o contacta a soporte.",
        );
        setSwitchingTenantId(null);
      }
    },
    [navigate, queryClient, setCurrentTenantId, switchingTenantId, userKey],
  );

  useEffect(() => {
    if (autoSwitchAttemptedRef.current || !workspaces.data || switchingTenantId)
      return;
    const rememberedTenantId = getRememberedTenantForUser(userKey);
    if (!rememberedTenantId) return;

    const rememberedWorkspace = workspaces.data.find(
      (workspace) => workspace.tenantId === rememberedTenantId,
    );
    if (!rememberedWorkspace) return;

    autoSwitchAttemptedRef.current = true;
    void selectTenant(rememberedWorkspace, "auto");
  }, [selectTenant, switchingTenantId, userKey, workspaces.data]);

  return (
    <div className="min-h-screen bg-bg grid place-items-center p-6">
      <Card padding={28} className="w-full max-w-2xl">
        {/* NO ELIMINAR — bloque S32-PROD · selección tenant bloqueante
            Razón: en producción no debe existir navegación autenticada sin tenant activo confirmado por backend. */}
        <div className="mb-6 flex items-start gap-4">
          <div className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-primary-soft text-primary-ink">
            <Icon name="briefcase" size={24} />
          </div>
          <div>
            <div className="font-serif text-2xl font-medium">
              Selecciona un tenant
            </div>
            <p className="mt-1 text-sm text-muted">
              Necesitamos activar un tenant antes de cargar cualquier módulo.
              Guardaremos tu elección en este navegador para restaurarla
              automáticamente en próximos ingresos.
            </p>
          </div>
        </div>

        {switchError && (
          <ErrorBanner
            title="No se pudo activar el tenant"
            message={switchError}
            className="mb-4"
          />
        )}

        {workspaces.isLoading && (
          <div className="space-y-2">
            <Skeleton width="100%" height={54} rounded="lg" />
            <Skeleton width="94%" height={54} rounded="lg" />
            <Skeleton width="88%" height={54} rounded="lg" />
          </div>
        )}

        {workspaces.isError && (
          <ErrorBanner
            title="No se pudieron cargar tus tenants"
            message="La sesión está activa, pero el catálogo de tenants no respondió."
            onRetry={() => workspaces.refetch()}
          />
        )}

        {!workspaces.isLoading &&
          !workspaces.isError &&
          (workspaces.data ?? []).length === 0 && (
            <EmptyState
              title="Sin tenants disponibles"
              description="Tu usuario no tiene memberships activos. Cierra sesión e ingresa con una cuenta habilitada."
              icon="briefcase"
              action={
                <Button variant="quiet" onClick={() => void logout()}>
                  Cerrar sesión
                </Button>
              }
            />
          )}

        <div className="space-y-2">
          {(workspaces.data ?? []).map((workspace) => {
            const switching = switchingTenantId === workspace.tenantId;
            return (
              <button
                key={workspace.tenantId}
                type="button"
                disabled={Boolean(switchingTenantId)}
                onClick={() => void selectTenant(workspace, "manual")}
                className="w-full rounded-lg border border-border bg-surface p-4 text-left transition hover:border-border-strong hover:bg-bg-2 disabled:cursor-wait disabled:opacity-70"
              >
                <div className="flex items-center gap-3">
                  <div className="grid h-9 w-9 shrink-0 place-items-center rounded-md bg-bg-2 font-serif text-sm font-bold">
                    {workspace.name[0]?.toUpperCase() ?? "?"}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold">
                      {workspace.name}
                    </div>
                    <div className="truncate text-xs text-muted">
                      {workspace.plan} · {workspace.role}
                    </div>
                  </div>
                  {switching && (
                    <Icon
                      name="spinner"
                      size={16}
                      className="animate-spin text-primary"
                    />
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </Card>
    </div>
  );
}
